import { Markdown } from './markdown.js';
import d from './DOM.js';
import network from './network.js';

interface IStrategy {
	id: number;
	userID: number | null;
	type: 'SYSTEM' | 'USER';
	label: string;
	content: string;
}

export default abstract class TradingStrategies {
	public static init() {
		const strategies: IStrategy[] = (window as any).initialStrategies || [];
		TradingStrategies.renderStrategies(strategies);
		d.e('click', () => { TradingStrategies.openModal(); }, d.get('btn-new-strategy'));
		
		d.e('click', async () => {
			const id = d.get<HTMLInputElement>('strategy-id').value;
			const label = d.get<HTMLInputElement>('strategy-label').value;
			const content = d.get<HTMLTextAreaElement>('strategy-content').value;
			
			if (!label) { return alert('Label is required'); }
			
			try {
				const res = await network.ap('/api/strategies/save', { id, label, content });
				
				if (res.ok) { window.location.reload(); }
				else { alert(res.payload.reason || 'Failed to save'); }
			} catch (err) {
				console.error(err);
				alert('Error saving strategy');
			}
		}, d.get('btn-save-strategy'));
		
		d.e('click', async () => {
			if (!confirm('Are you sure you want to delete this strategy?')) { return; }
			const id = d.get<HTMLInputElement>('strategy-id').value;
			
			try {
				const res = await network.ad(`/api/strategies/${id}`);
				
				if (res.ok) { window.location.reload(); }
				else { alert(res.payload.reason || 'Failed to delete'); }
			} catch (err) {
				console.error(err);
				alert('Error deleting strategy');
			}
		}, d.get('btn-delete-strategy'));
		
		// Markdown Toggle
		d.e('click', () => {
			d.get<HTMLButtonElement>('btn-edit-md')?.classList.add('active');
			d.get<HTMLButtonElement>('btn-preview-md')?.classList.remove('active');
			d.get<HTMLTextAreaElement>('strategy-content')!.style.display = 'block';
			d.get<HTMLDivElement>('strategy-preview')!.style.display = 'none';
		}, d.get('btn-edit-md'));
		
		d.e('click', () => {
			d.get<HTMLButtonElement>('btn-edit-md')?.classList.remove('active');
			d.get<HTMLButtonElement>('btn-preview-md')?.classList.add('active');
			d.get<HTMLTextAreaElement>('strategy-content')!.style.display = 'none';
			const preview = d.get<HTMLDivElement>('strategy-preview')!;
			preview.style.display = 'block';
			preview.innerHTML = Markdown.parse(d.get<HTMLTextAreaElement>('strategy-content').value);
		}, d.get('btn-preview-md'));
		
		// Close modal
		d.qsa<HTMLButtonElement>('.close, .close-modal').forEach(el => {
			el.addEventListener('click', () => {
				d.get<HTMLDivElement>('modal-strategy')!.classList.add('hidden');
			});
		});
	}
	
	private static renderStrategies(strategies: IStrategy[]) {
		const userContainer = d.get<HTMLDivElement>('user-strategies-list');
		const systemContainer = d.get<HTMLDivElement>('system-strategies-list');
		const userSection = d.get<HTMLDivElement>('user-strategies-section');
		
		if (!userContainer || !systemContainer) { return; }
		
		const userStrats = strategies.filter(s => s.type === 'USER');
		const systemStrats = strategies.filter(s => s.type === 'SYSTEM');

		// Handle User Section visibility
		if (userStrats.length > 0) {
			userSection!.style.display = 'block';
			userContainer.innerHTML = userStrats.map(s => TradingStrategies.getStrategyCard(s)).join('');
		} else {
			userSection!.style.display = 'none';
		}

		// Always render systems
		systemContainer.innerHTML = systemStrats.map(s => TradingStrategies.getStrategyCard(s)).join('');
			
		d.qsa<HTMLButtonElement>('.btn-edit-strategy').forEach(btn => {
			btn.addEventListener('click', (e) => {
				const id = (e.target as HTMLElement).dataset.id;
				const strategy = strategies.find(s => s.id == Number(id));
				if (strategy) { TradingStrategies.openModal(strategy); }
			});
		});
	}

	private static getStrategyCard(s: IStrategy) {
		return `
			<div class="card strategy-card" style="position: relative;">
				<div class="card-header" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
					<h3 style="margin: 0;">${s.label}</h3>
					<span class="badge ${s.type === 'SYSTEM' ? 'badge-info' : 'badge-success'}">${s.type}</span>
				</div>
				<div class="card-body" style="max-height: 100px; overflow: hidden; text-overflow: ellipsis; margin-bottom: 10px; color: var(--theme-text-muted);">
					${Markdown.parse(s.content).replace(/<[^>]*>/g, '').substring(0, 100)}...
				</div>
				<button class="btn-secondary btn-sm btn-edit-strategy" data-id="${s.id}">View / Edit</button>
			</div>
		`;
	}
	
	private static openModal(strategy?: IStrategy) {
		const modal = d.get<HTMLDivElement>('modal-strategy')!;
		const title = d.get<HTMLDivElement>('modal-strategy-title')!;
		const idInput = d.get<HTMLInputElement>('strategy-id');
		const labelInput = d.get<HTMLInputElement>('strategy-label');
		const contentInput = d.get<HTMLTextAreaElement>('strategy-content');
		const deleteBtn = d.get<HTMLButtonElement>('btn-delete-strategy')!;
		const saveBtn = d.get<HTMLButtonElement>('btn-save-strategy')!;
		
		if (strategy) {
			title.textContent = strategy.type === 'SYSTEM' ? 'View Strategy' : 'Edit Strategy';
			idInput.value = strategy.id.toString();
			labelInput.value = strategy.label;
			contentInput.value = strategy.content;
			
			if (strategy.type === 'SYSTEM') {
				labelInput.disabled = true;
				contentInput.disabled = true;
				deleteBtn.style.display = 'none';
				saveBtn.style.display = 'none';
			} else {
				labelInput.disabled = false;
				contentInput.disabled = false;
				deleteBtn.style.display = 'block';
				saveBtn.style.display = 'block';
			}
			
			// Default to preview for existing strategies
			d.get<HTMLButtonElement>('btn-preview-md')?.click();
		} else {
			title.textContent = 'New Strategy';
			idInput.value = '';
			labelInput.value = '';
			contentInput.value = '';
			labelInput.disabled = false;
			contentInput.disabled = false;
			deleteBtn.style.display = 'none';
			saveBtn.style.display = 'block';
			
			// Default to edit for new strategies
			d.get<HTMLButtonElement>('btn-edit-md')?.click();
		}
		
		modal.classList.remove('hidden');
	}
}
